<script lang="ts" setup>
import { useDefaultStore } from "@/store";
import api from "@/api";
import { useToast } from "primevue/usetoast";
import { $t } from "@/i18n";
import { debounce } from "@/assets/js/helpers";
import { useConfirm } from "primevue/useconfirm";
// import type { IUserList, IPermissions } from "@/api/types";
import { useSessionStore } from "@/store/session";
import type { TEntity } from "@/api/types";

definePage({
	name: "ManageEntities",
	meta: {
		name: "Entities Management",
	},
});

const confirm = useConfirm();
const toast = useToast();
const store = useDefaultStore();
const sessionStore = useSessionStore();
const searchValue = ref<string | null>(null);
const selectedProduct = ref();
const loadingDelete = ref(false);

const getEntitiesDebounce = () => {
	debounce(store.getEntities({ search: searchValue.value }), 300, true);
};

async function deleteEntity(ent_id: number) {
	loadingDelete.value = true;
	const params = {
		ent_id,
	};
	try {
		const res = await api.entitiesRemove(params);
		toast.add({
			severity: "success",
			summary: $t("app.manageTags.success.deleteTag.title"),
			detail: $t("app.manageTags.success.deleteTag.msg"),
			life: 3000,
		});
		store.getEntities({
			search: searchValue.value,
		});
		console.log(res.data.data);
	} catch (err: any) {
		toast.add({
			severity: "error",
			summary: $t("app.postForm.error.default.title"),
			detail: $t("app.postForm.error.default.msg"),
			life: 3000,
		});
		console.warn(err.message);
	} finally {
		loadingDelete.value = false;
	}
}

const confirmDelete = (ent_id: number) => {
	confirm.require({
		message: $t("app.manageTags.deleteTag.msg"),
		header: $t("app.manageTags.deleteTag.header"),
		rejectLabel: $t("app.basic.cancel"),
		rejectProps: {
			label: $t("app.basic.cancel"),
			severity: "secondary",
			outlined: true,
		},
		acceptProps: {
			label: $t("app.basic.delete"),
			severity: "danger",
		},
		accept: async () => {
			await deleteEntity(ent_id);
		},
	});
};

function openEditEntityModal(ent: TEntity) {
	store.selectedEntity = ent;

	store.editEntityVisible = true;
}

async function editEntity() {
	store.getEntities({ search: searchValue.value });
}
</script>

<template>
	<div class="admin-section-outer-wrapper">
		<div class="admin-section-inner-wrapper">
			<div class="admin-section-header-wrapper">
				<div class="admin-section-header-heading">
					{{ $t("app.manageTags.title") }}
				</div>
				<div class="admin-section-header-filters-wrapper">
					<IconField
						:pt="{
							root: {
								style: 'width: 333px; max-width: 90%; height: 38px;',
							},
						}"
					>
						<InputIcon>
							<i-fa-magnifying-glass />
						</InputIcon>
						<InputText
							v-model="searchValue"
							@value-change="getEntitiesDebounce"
							:placeholder="$t('app.manageTags.searchPlaceholder')"
							fluid
						/>
					</IconField>
					<CreateEntity @create-entity="store.getEntities" />
					<EntityEdit @edit-entity="editEntity" />
				</div>
			</div>
			<div
				class="admin-section-content-wrapper"
				style="max-width: 600px"
			>
				<DataTable
					v-if="!store.entityList"
					:value="[
						{ id: 1 },
						{ id: 2 },
						{ id: 3 },
						{ id: 4 },
						{ id: 5 },
						{ id: 6 },
						{ id: 7 },
						{ id: 8 },
						{ id: 9 },
						{ id: 10 },
					]"
					stripedRows
					scrollable
					scrollHeight="500px"
				>
					<Column
						:header="$t('app.manageTags.nameLabel')"
						style="width: 100%"
					>
						<template #body>
							<Skeleton height="29px"></Skeleton>
						</template>
					</Column>
					<Column style="width: 82px">
						<template #body>
							<Button
								severity="danger"
								variant="outlined"
								rounded
							>
								<template #icon>
									<i-fa-trash-can></i-fa-trash-can>
								</template>
							</Button>
						</template>
					</Column>
				</DataTable>
				<DataTable
					v-else
					:value="store.entityList"
					stripedRows
					selectionMode="single"
					v-model:selection="selectedProduct"
					scrollable
					scrollHeight="500px"
					:virtualScrollerOptions="{ itemSize: 50 }"
				>
					<Column
						field="ent_name"
						:header="$t('app.manageTags.nameLabel')"
						style="width: 100%"
					>
						<template #editor="{ data, field }">
							<InputText v-model="data[field]" />
						</template>
					</Column>
					<Column
						v-if="sessionStore.can('edit-tag')"
						:rowEditor="true"
						style="min-width: 120px"
						bodyStyle="text-align:end"
					>
						<template #body="{ data }">
							<Button
								variant="outlined"
								rounded
								@click="openEditEntityModal(data)"
							>
								<template #icon>
									<i-fa-pencil></i-fa-pencil>
								</template>
							</Button>
						</template>
					</Column>
					<Column
						v-if="sessionStore.can('delete-tag')"
						style="display: flex; justify-content: center; width: 82px"
					>
						<template #header><i-fa-trash-can></i-fa-trash-can></template>
						<template #body="{ data }">
							<Button
								severity="danger"
								variant="outlined"
								rounded
								@click="confirmDelete(data.ent_id)"
								:loading="loadingDelete"
							>
								<template #icon>
									<i-fa-trash-can></i-fa-trash-can>
								</template>
							</Button>
						</template>
					</Column>
				</DataTable>
			</div>
		</div>
	</div>
</template>

<style lang="scss">
.p-virtualscroller-inline {
	&::-webkit-scrollbar {
		background-color: $scrollbar-color;
		width: 5px;
		height: 5px;
	}

	&::-webkit-scrollbar-thumb {
		background-color: $scrollbar-color-thumb;
		border-radius: 5px;
	}
}

.p-datatable-column-title {
	font-size: 11px;
	font-weight: 700;
	line-height: 12.89px;
	color: $text-color-lighter;
}
</style>
